import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QuillEditor, type QuillDelta } from '@/components/custom/quill-editor';
import { AssetPicker } from '@/components/shared/asset-picker';
import type { Setting, SettingCreateInput, SettingUpdateInput } from '@/types/entities';

const SETTING_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'double', label: 'Number' },
  { value: 'real', label: 'Real' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'image', label: 'Image' },
  { value: 'richtext', label: 'Rich Text' },
] as const;

type FormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SettingCreateInput | SettingUpdateInput) => Promise<void>;
  onCancel?: () => void;
  setting?: Setting;
  tenantSlug: string;
  canManagePrivateSettings?: boolean;
  canEditType?: boolean;
}

// Parse Quill Delta JSON format, return Delta object
const parseRichTextValue = (value: unknown): QuillDelta => {
  if (!value) return { ops: [{ insert: '' }] };
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed.ops && Array.isArray(parsed.ops)) {
        return parsed as QuillDelta;
      }
    } catch {
      // Not valid JSON, return empty Delta
    }
  }
  if (typeof value === 'object' && value !== null) {
    const delta = value as Partial<QuillDelta>;
    if (delta.ops && Array.isArray(delta.ops)) {
      return delta as QuillDelta;
    }
  }
  return { ops: [{ insert: '' }] };
};

const hasRichTextContent = (delta: QuillDelta): boolean => {
  const text = delta.ops?.map(op => {
    if (typeof op.insert === 'string') return op.insert;
    return '';
  }).join('') || '';
  return text.trim().length > 0 || delta.ops?.some(op => 'insert' in op && typeof op.insert === 'object');
};

export function Form({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  setting,
  tenantSlug,
  canManagePrivateSettings = false,
  canEditType = true,
}: FormProps) {
  const [attribute, setAttribute] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [value, setValue] = useState<string | number | QuillDelta | null>('');
  const [type, setType] = useState<string>('text');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (setting) {
      setAttribute(setting.attribute);
      setDisplayName(setting.display_name || setting.attribute);
      setType(setting.type || 'text');
      setVisibility(setting.visibility || 'public');

      // Parse value based on type
      if (setting.type === 'richtext') {
        setValue(parseRichTextValue(setting.value));
      } else if (setting.type === 'image') {
        const numValue = setting.value ? Number(setting.value) : null;
        setValue(isNaN(numValue as number) ? null : numValue);
      } else if (setting.type === 'integer' || setting.type === 'double' || setting.type === 'real') {
        const numValue = Number(setting.value);
        setValue(isNaN(numValue) ? '' : numValue);
      } else {
        setValue(setting.value);
      }
    } else {
      setAttribute('');
      setDisplayName('');
      setValue('');
      setType('text');
      setVisibility('public');
    }
    setError(null);
  }, [setting, open]);

  const renderValueField = () => {
    if (type === 'richtext') {
      const deltaValue = parseRichTextValue(value);
      return (
        <QuillEditor
          value={deltaValue}
          onChange={(nextDelta) => setValue(nextDelta)}
          tenantSlug={tenantSlug}
          placeholder="Write rich content..."
        />
      );
    }

    if (type === 'image') {
      return (
        <AssetPicker
          tenantSlug={tenantSlug}
          value={typeof value === 'number' ? value : value ? Number(value) : null}
          onChange={(nextValue) => setValue(nextValue)}
        />
      );
    }

    let inputType = 'text';
    if (type === 'integer' || type === 'double' || type === 'real') {
      inputType = 'number';
    } else if (type === 'datetime') {
      inputType = 'datetime-local';
    }

    return (
      <Input
        id="value"
        type={inputType}
        value={String(value ?? '')}
        onChange={(e) => {
          if (type === 'integer' || type === 'double' || type === 'real') {
            const numValue = parseFloat(e.target.value);
            setValue(isNaN(numValue) ? '' : numValue);
          } else {
            setValue(e.target.value);
          }
        }}
        placeholder={
          type === 'datetime'
            ? 'Select date and time'
            : type === 'integer' || type === 'double' || type === 'real'
              ? 'Enter a number'
              : 'e.g., My Website'
        }
        disabled={isSubmitting}
      />
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!attribute.trim()) {
      setError('Attribute is required');
      return;
    }

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    // Validate attribute format (alphanumeric, underscore, hyphen)
    if (!/^[a-zA-Z0-9_-]+$/.test(attribute)) {
      setError('Attribute must contain only letters, numbers, underscores, and hyphens');
      return;
    }

    // Validate value based on type
    if (type === 'richtext') {
      const deltaValue = parseRichTextValue(value);
      if (!hasRichTextContent(deltaValue)) {
        setError('Rich text content is required');
        return;
      }
    } else if (type === 'image') {
      // Image can be null/empty, so no validation needed
    } else if (type === 'integer' || type === 'double' || type === 'real') {
      if (value === '' || value === null || value === undefined) {
        setError('Value is required');
        return;
      }
      if (isNaN(Number(value))) {
        setError('Please enter a valid number');
        return;
      }
    } else if (type === 'datetime') {
      if (!value || String(value).trim() === '') {
        setError('Date and time is required');
        return;
      }
    } else {
      // text type
      if (!value || String(value).trim() === '') {
        setError('Value is required');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Serialize value based on type
      let serializedValue: string;
      if (type === 'richtext') {
        const deltaValue = parseRichTextValue(value);
        serializedValue = JSON.stringify(deltaValue);
      } else if (type === 'image') {
        serializedValue = value ? String(value) : '';
      } else if (type === 'datetime') {
        // Ensure ISO format
        const dateValue = new Date(String(value));
        serializedValue = isNaN(dateValue.getTime()) ? String(value) : dateValue.toISOString();
      } else {
        serializedValue = String(value);
      }

      const data: SettingCreateInput | SettingUpdateInput = {
        attribute: attribute.trim(),
        display_name: displayName.trim(),
        value: serializedValue,
        visibility: canManagePrivateSettings ? visibility : 'public',
      };

      if (canEditType) {
        data.type = type || 'text';
      }

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setAttribute('');
    setDisplayName('');
    setValue('');
    setType('text');
    setVisibility('public');
    setError(null);
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{setting ? 'Edit Setting' : 'Add Setting'}</DialogTitle>
          <DialogDescription>
            {setting
              ? 'Update the setting information below.'
              : 'Enter the information for the new setting.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="attribute">Attribute</Label>
            <Input
              id="attribute"
              value={attribute}
              onChange={(e) => setAttribute(e.target.value)}
              placeholder="e.g., site_title"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Site Title"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={type}
              onValueChange={setType}
              disabled={isSubmitting || !canEditType}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {SETTING_TYPES.map((settingType) => (
                  <SelectItem key={settingType.value} value={settingType.value}>
                    {settingType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canManagePrivateSettings && (
            <div className="grid gap-2">
              <Label htmlFor="visibility">Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(nextValue) => setVisibility(nextValue as 'public' | 'private')}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="value">Value</Label>
            {renderValueField()}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : setting ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
